import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'

import UserAvatar from '../components/mini/UserAvatar'
import InlineErrorMessage from '../components/auth/InlineErrorMessage'
import ButtonA from '../components/typography/buttons/ButtonA'
import Caption1 from '../components/typography/captions/Caption1'
import H3 from '../components/typography/headers/H3'
import AuthWrapper from '../components/auth/AuthWrapper'
import InputHint from '../components/typography/InputHint'
import JustifyCenter from '../components/presentational/JustifyCenter'
import P2 from '../components/typography/paragraphs/P2'
import PasswordField from '../components/typography/PasswordField'
import TextButton from '../components/typography/buttons/TextButton'
import CheckmarkIcon from '../static/images/icon_checkmark.svg'

import { changePassword } from '../actions/account'
import { fetchDataAndRedirectUser } from '../actions/combined'
import { logoutAndRedirect, userNeedsToResetPassword } from '../actions/user'

class ChangePassword extends Component {
  constructor(props) {
    super(props)

    this.state = {
      currentPassword: '',
      newPassword: '',
      error: null
    }

    this.handleCurrentPasswordChangeBound =
      this.handleCurrentPasswordChange.bind(this)
    this.handleNewPasswordChangeBound = this.handleNewPasswordChange.bind(this)
    this.shouldButtonBeDisabledBound = this.shouldButtonBeDisabled.bind(this)
    this.onReturnToLoginClickBound = this.onReturnToLoginClick.bind(this)
    this.checkForSubmitBound = this.checkForSubmit.bind(this)
    this.onChangePasswordClickBound = this.onChangePasswordClick.bind(this)
    this.onContinueClickBound = this.onContinueClick.bind(this)
    this.handleFormSubmitBound = this.handleFormSubmit.bind(this)

    window.scrollTo(0, 0)
  }

  render() {
    const { error } = this.state
    const { isChangingPassword, user, success, failure } = this.props
    return (
      <AuthWrapper hideIcon>
        {!isChangingPassword && !success && (
          <React.Fragment>
            <JustifyCenter>
              <UserAvatar
                wrapperClass="change-password-user-avatar"
                style={{ marginBottom: '20px', height: '39px', width: '39px' }}
                photoUrl={user ? user.photo.url : null}
              />
              <H3 style={{ marginBottom: '10px' }}>
                Before you continue, please change your password.
              </H3>
              <Caption1 style={{ marginBottom: '30px' }}>
                Use something unique and memorable.
              </Caption1>
            </JustifyCenter>
            <form style={{ textAlign: 'left' }}>
              <PasswordField
                label="Current Password"
                onChange={this.handleCurrentPasswordChangeBound}
                onKeyPress={this.checkForSubmitBound}
                tooltipId="currentPassword"
              />
              {(error || failure) && (
                <InlineErrorMessage style={{ bottom: '22px', left: '10px' }}>
                  {error
                    ? 'Current password is incorrect.'
                    : 'There was a problem changing your password. Please try again later.'}
                </InlineErrorMessage>
              )}
              <PasswordField
                label="New Password"
                onChange={this.handleNewPasswordChangeBound}
                onKeyPress={this.checkForSubmitBound}
                tooltipId="newPassword"
                style={{ marginBottom: '38px' }}
              />
              <InputHint
                style={{
                  position: 'relative',
                  bottom: '32px',
                  left: '13px'
                }}
              >
                Minimum 8 characters
              </InputHint>
              <JustifyCenter>
                <ButtonA
                  type="submit"
                  onClick={this.onChangePasswordClickBound}
                  disabled={this.shouldButtonBeDisabledBound()}
                  style={{ marginBottom: '22px', width: 'unset' }}
                >
                  Change Password
                </ButtonA>
              </JustifyCenter>
            </form>
            <TextButton
              style={{ marginTop: 'auto' }}
              onClick={this.onReturnToLoginClickBound}
            >
              Return to Login
            </TextButton>
          </React.Fragment>
        )}
        {!isChangingPassword && success && (
          <JustifyCenter>
            <div
              id="changePasswordCheckmark"
              style={{
                background: `url(${CheckmarkIcon}) no-repeat`,
                display: 'inline-block',
                height: '50px',
                width: '50px',
                marginBottom: '28px'
              }}
            />
            <H3 style={{ marginBottom: '16px' }}>Password Changed!</H3>
            <P2 style={{ marginBottom: '33px' }}>
              Remember to use your new password the next time you log in.
            </P2>
            <ButtonA onClick={this.onContinueClickBound}>Continue</ButtonA>
          </JustifyCenter>
        )}
        {isChangingPassword && (
          <div className="loading" style={{ minHeight: '445px' }} />
        )}
      </AuthWrapper>
    )
  }

  checkForSubmit(e) {
    if (
      !this.shouldButtonBeDisabled() &&
      (e.which === 13 || e.keyCode === 13)
    ) {
      this.onChangePasswordClick(e)
    }
  }

  handleCurrentPasswordChange(e) {
    this.setState({ currentPassword: e.target.value.trim() })
  }

  handleNewPasswordChange(e) {
    this.setState({ newPassword: e.target.value.trim() })
  }

  shouldButtonBeDisabled() {
    return (
      this.state.currentPassword.trim().length < 1 ||
      this.state.newPassword.trim().length < 8
    )
  }

  async onReturnToLoginClick() {
    const { dispatch } = this.props
    await dispatch(userNeedsToResetPassword(false))
    this.props.dispatch(logoutAndRedirect())
  }

  async onChangePasswordClick(e) {
    e.preventDefault()
    const { currentPassword, newPassword } = this.state
    const { passwordValidation } = this.props
    if (passwordValidation === currentPassword) {
      if (!this.shouldButtonBeDisabled()) {
        this.props.dispatch(changePassword(currentPassword, newPassword))
      }
    } else {
      this.setState(() => {
        return {
          error: 'Current password is incorrect.'
        }
      })
    }
  }

  async onContinueClick() {
    await this.props.dispatch(userNeedsToResetPassword(false))
    this.props.dispatch(fetchDataAndRedirectUser(this.props.user))
  }

  handleFormSubmit(e) {
    e.preventDefault()
    return false
  }
}

export const mapStateToProps = state => ({
  isChangingPassword: state.account.isChangingPassword,
  failure: state.account.passwordChangeFailure,
  success: state.account.passwordChangeSuccess,
  user: state.user.user,
  passwordValidation: sessionStorage.getItem('password'),
  userNeedsToResetPassword: state.user.userNeedsToResetPassword
})

ChangePassword.propTypes = {
  dispatch: PropTypes.func,
  params: PropTypes.object,
  isChangingPassword: PropTypes.bool.isRequired,
  failure: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  passwordValidation: PropTypes.string.isRequired,
  userNeedsToResetPassword: PropTypes.bool.isRequired
}

export default connect(mapStateToProps)(ChangePassword)
export { ChangePassword as ChangePasswordDisconnected } // pure component - used in tests



// WEBPACK FOOTER //
// ./src/containers/ChangePassword.js